import { z } from 'zod';

/**
 * Utility schema for nullable string fields (common in API responses)
 */
export const nullableString = z.string().trim().nullish();

/**
 * Utility schema for nullable boolean fields (common in API responses)
 */
export const nullableBoolean = z.boolean().nullish();

/**
 * Utility schema for nullable number fields (common in API responses)
 */
export const nullableNumber = z.number().nullish();

/**
 * Schema for arrays containing nullable numbers
 * Used numeric arrays that may contain null values
 */
export const nullableNumberArray = z.array(z.number().nullable()).nullish();

/**
 * Schema for nullable objects
 */
export const nullableObject = z.object({}).nullish();

/**
 * Base schema for all DeckManagement entities.
 * All DeckManagement entities share this common pk/sk structure.
 * Use this as a base for extending with entity-specific fields.
 */
export const BaseDeckManagementEntry = z.object({
  pk: z.string(),
  sk: z.string(),
});

/**
 * Type for the base DeckManagement entity structure
 */
export type BaseDeckManagementEntryType = z.infer<typeof BaseDeckManagementEntry>;

// KPIDriversInput schema
export const KPIDriversInputSchema = z
  .object({
    account_hierarchy: nullableString,
    account: nullableString,
    location_hierarchy: nullableString,
    location: nullableString,
    project_hierarchy: nullableString,
    project: nullableString,
    company_hierarchy: nullableString,
    company: nullableString,
    product_hierarchy: nullableString,
    product: nullableString,
    revgp_hierarchy: nullableString,
    revgp: nullableString,
    datasource_hierarchy: nullableString,
    datasource: nullableString,
    channel_hierarchy: nullableString,
    channel: nullableString,
    costcenter_hierarchy: nullableString,
    costcenter: nullableString,
    costtype: nullableString,
    costpool: nullableString,
  })
  .nullable()
  .optional();

/**
 * Schema for validating Comment objects.
 * Defines the structure and types of Comment properties.
 * Matches the Comment GraphQL type requirements.
 */
export const CommentSchema = z.object({
  commentId: z.string(),
  user: z.string(),
  commentDesc: z.string(),
  timestamp: z.string(),
});

/**
 * Configuration for creating changeset operations schemas
 */
export interface ChangeSetOperationsConfig<TCreate, TUpdate, TDelete> {
  /** Schema for create operations (typically omits pk/sk) */
  create: z.ZodType<TCreate>;
  /** Schema for update operations (includes all fields) */
  update: z.ZodType<TUpdate>;
  /** Schema for delete operations (typically only pk/sk) */
  delete?: z.ZodType<TDelete>;
}

/**
 * Factory function to create a ChangeSet operations schema for a specific entity type.
 * This provides a consistent structure for create, update, and delete operations.
 *
 * @param config - Configuration object with create, update, and optional delete schemas
 * @returns Zod schema for the complete changeset operations
 */
export const createChangeSetOperations = <TCreate, TUpdate, TDelete = BaseDeckManagementEntryType>(
  config: ChangeSetOperationsConfig<TCreate, TUpdate, TDelete>,
) => {
  const { create, update, delete: deleteSchema = BaseDeckManagementEntry as unknown as z.ZodType<TDelete> } = config;

  return z.object({
    create: z.array(create).optional(),
    update: z.array(update).optional(),
    delete: z.array(deleteSchema).optional(),
  });
};

/**
 * Enum defining all possible ChangeSet targets for the DeckManagement service.
 * These correspond to different entity types that can be modified through change sets.
 *
 * @see https://tiny.amazon.com/ftm4p0pl/codeamazpackGFTAblob968esrc
 */
export enum ChangeSetTargets {
  toc = 'toc',
  deck = 'deck',
  scenario = 'scenario',
  page = 'page',
  deckline = 'deckline',
  deckcolumn = 'deckcolumn',
  footnote = 'footnote',
  kpi = 'kpi',
  audit = 'audit',
}

/**
 * @deprecated Use createChangeSetOperations factory instead
 * Legacy base schema - kept for backward compatibility
 */
export const ChangeSetBase = z.object({
  create: z.array(z.any()).default([]),
  update: z.array(z.any()).default([]),
  delete: z.array(z.any()).default([]),
});
